import { FC, useEffect, useState } from 'react';

import { formatPrice } from '@shared/utils/formatPrice';
import { Link } from 'react-router-dom';

import { OrderStatKind, OrderStatOrder } from './adminOrderStat.types';
import {
  isCanceledOrder,
  netAmountOf,
  netQuantityOf,
  orderBrandNameOf,
  orderDayKey,
  orderDeceasedNameOf,
  orderItemNameOf,
} from './adminOrderStat.util';
import { useAnalyticsBrandNames } from './useAnalyticsBrandNames';

//* 표/달력의 한 칸에 속한 주문들을 열어보는 드릴다운 (부고 탭의 상가 목록과 같은 틀).

//* 한 번에 그리는 최대 건수 — 반기 조회는 주문이 수천~2만 건이라 전량 렌더하면 화면이 멎는다
const PAGE_SIZE = 500;

const feventIdOf = (order: OrderStatOrder): string | null => {
  const fevent = order.fevent;
  if (!fevent) return null;
  return typeof fevent === 'string' ? fevent : fevent._id;
};

export const AdminOrderListModal: FC<{
  kind: OrderStatKind;
  title: string;
  subtitle?: string;
  orders: OrderStatOrder[];
  onClose: () => void;
}> = function AdminOrderListModal({ kind, title, subtitle, orders, onClose }) {
  //* 상조명은 이름 맵으로 붙인다 (문서에 populate가 없다). 맵은 세션 캐시라 추가 조회 없음
  const { resolveBrandName } = useAnalyticsBrandNames();

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  //* 같은 모달이 열린 채 다른 칸으로 바뀌어도 처음부터 다시 세도록
  useEffect(() => setVisibleCount(PAGE_SIZE), [orders]);
  const visibleOrders = orders.slice(0, visibleCount);
  const restCount = orders.length - visibleOrders.length;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-3">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative my-3 flex max-h-[92vh] w-full max-w-[900px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-18 font-bold text-gray-900">{title}</p>
            <p className="mt-0.5 text-13 tabular-nums text-gray-600">
              {`${subtitle ? `${subtitle} · ` : ''}${orders.length.toLocaleString()}건`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="ml-3 shrink-0 rounded-lg px-2.5 py-1 text-20 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto bg-gray-50 p-3">
          {orders.length === 0 ? (
            <p className="py-10 text-center text-14 text-gray-500">
              해당 조건의 주문이 없습니다.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {visibleOrders.map((order) => {
                const canceled = kind === 'gift' && isCanceledOrder(order);
                const feventId = feventIdOf(order);
                const deceased = orderDeceasedNameOf(order);
                const itemName = orderItemNameOf(order, kind);
                return (
                  <li key={order._id} className="rounded-lg bg-white px-3 py-2 shadow-smd">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-16 font-semibold text-gray-900">
                          <span className="tabular-nums text-gray-500">
                            {orderDayKey(order, kind) ?? '날짜 없음'}
                          </span>
                          <span className="ml-2 tabular-nums">
                            {formatPrice(Math.round(netAmountOf(order, kind)))}원
                          </span>
                          {canceled && (
                            <span className="ml-2 rounded bg-red-50 px-1.5 py-0.5 text-12 font-medium text-red-600">
                              전체취소
                            </span>
                          )}
                        </p>
                        <p className="mt-0.5 truncate text-13 text-gray-600">
                          {orderBrandNameOf(order, resolveBrandName)}
                          {deceased ? ` · 故 ${deceased}` : ''}
                          {itemName ? ` · ${itemName}` : ''}
                        </p>
                      </div>
                      {feventId && (
                        //* 관리자용 부고정보 — 이 주문이 붙은 상가로 넘어간다
                        <Link
                          to={`/fevent/${feventId}/admin-info`}
                          className="shrink-0 rounded-lg bg-gray-100 px-3 py-1.5 text-13 font-medium text-gray-700 hover:bg-gray-200"
                        >
                          상가
                        </Link>
                      )}
                    </div>

                    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-13 tabular-nums text-gray-500">
                      <span>수량 {netQuantityOf(order, kind).toLocaleString()}</span>
                      {order.paymentDetail?.status && (
                        <span>상태 {order.paymentDetail.status}</span>
                      )}
                      {kind === 'gift' && order.receiverName && (
                        <span className="truncate">수령 {order.receiverName}</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {restCount > 0 && (
            <button
              type="button"
              onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
              className="mt-2 w-full rounded-lg bg-white py-2.5 text-14 font-medium text-gray-700 shadow-smd hover:bg-gray-100"
            >
              {`더보기 (${restCount.toLocaleString()}건 남음)`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
